import Table, { type TableColumn } from "@/components/common/Table";
import KnowledgeBaseHeader from "@/components/knowledge-base/KnowledgeBaseHeader";
import QuestionForm from "@/components/knowledge-base/QuestionForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { knowledgeBaseService } from "@/services/knowledgeBase.service/knowledgeBase.api";
import type {
  KnowledgeBase,
  KnowledgeBaseQuestion,
} from "@/types/knowledgeBase";
import { Edit } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export default function KnowledgeBaseQuestions() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase>({
    _id: id || "",
    name: "",
    description: "",
    questionsCount: 0,
    isActive: true,
  });
  const [questions, setQuestions] = useState<KnowledgeBaseQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<KnowledgeBaseQuestion>({
    _id: "",
    question: "",
    answer: "",
    isActive: true,
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<string>("question");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const columns: TableColumn<KnowledgeBaseQuestion>[] = [
    {
      header: "Pergunta",
      accessor: "question",
      sortable: true,
    },
    {
      header: "Resposta",
      accessor: "answer",
      cell: (row) => (
        <div className="max-w-md truncate" title={row.answer}>
          {row.answer}
        </div>
      ),
    },
    {
      header: "Status",
      accessor: "isActive",
      cell: (row) => (
        <Badge
          variant={row.isActive ? "default" : "secondary"}
          className="cursor-pointer"
          onClick={() => handleToggleActive(row._id)}
        >
          {row.isActive ? "Ativo" : "Inativo"}
        </Badge>
      ),
    },
    {
      header: "Ações",
      accessor: "_id",
      cell: (row) => (
        <div className="flex gap-2 justify-end">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleOpenEditDialog(row)}
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const fetchKnowledgeBase = useCallback(async () => {
    if (!id) return;

    const [res, error] = await knowledgeBaseService.getOne(id);

    if (error) {
      console.error("Erro ao carregar base de conhecimento:", error);
      return;
    }

    if (res) {
      setKnowledgeBase(res);
    }
  }, [id]);

  const fetchQuestions = useCallback(async () => {
    if (!id) return;

    setLoading(true);

    const query: {
      page: number;
      limit: number;
      s_and_question?: string;
    } = {
      page,
      limit,
    };

    if (searchQuery) {
      query.s_and_question = searchQuery;
    }

    try {
      const [questionsRes, questionsErr] = await knowledgeBaseService.getQuestions(id, query);

      if (questionsErr) {
        console.error("Erro ao carregar perguntas:", questionsErr);
        return;
      }

      if (questionsRes) {
        setQuestions(questionsRes.result);
        setTotalPages(questionsRes.qtdPages);
        setTotalItems(questionsRes.total);
      }
    } catch (error) {
      console.error("Erro ao carregar perguntas:", error);
    } finally {
      setLoading(false);
    }
  }, [id, page, limit, searchQuery]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleSort = (column: string, direction: "asc" | "desc") => {
    setSortColumn(column);
    setSortDirection(direction);
    setPage(1);
  };

  const handleAddQuestion = async (question: KnowledgeBaseQuestion) => {
    try {
      const [res, error] = await knowledgeBaseService.newQuestion(id!, question);

      if (error) {
        console.error("Erro ao adicionar pergunta:", error);
        return;
      }

      if (res) {
        await fetchQuestions();
        setCurrentQuestion({
          _id: "",
          question: "",
          answer: "",
          isActive: true,
        });
        setIsAddDialogOpen(false);
        toast.success("Pergunta adicionada com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao adicionar pergunta:", error);
    }
  };

  const handleEditQuestion = async (question: KnowledgeBaseQuestion) => {
    try {
      const [res, error] = await knowledgeBaseService.updateQuestion(
        id!,
        question._id,
        question
      );

      if (error) {
        console.error("Erro ao atualizar pergunta:", error);
        return;
      }

      if (res) {
        setCurrentQuestion({
          _id: "",
          question: "",
          answer: "",
          isActive: true,
        });
        await fetchQuestions();
        setIsEditDialogOpen(false);
        toast.success("Pergunta atualizada com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao atualizar pergunta:", error);
    }
  };

  const handleToggleActive = async (questionId: string) => {
    const question = questions.find((q) => q._id === questionId);
    if (!question) return;

    try {
      const [res, error] = await knowledgeBaseService.updateQuestion(
        id!,
        questionId,
        { isActive: !question.isActive }
      );

      if (error) {
        console.error("Erro ao atualizar status da pergunta:", error);
        return;
      }

      if (res) {
        setQuestions(
          questions.map((q) => (q._id === questionId ? res : q))
        );
        toast.success("Status da pergunta atualizado!");
      }
    } catch (error) {
      console.error("Erro ao atualizar status da pergunta:", error);
    }
  };

  const handleOpenEditDialog = (question: KnowledgeBaseQuestion) => {
    setCurrentQuestion(question);
    setIsEditDialogOpen(true);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  useEffect(() => {
    fetchKnowledgeBase();
  }, [fetchKnowledgeBase]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  return (
    <>
      <KnowledgeBaseHeader
        knowledgeBase={knowledgeBase}
        questionsCount={totalItems}
        onBack={() => navigate("/knowledge-base")}
        onAddQuestion={() => {
          setCurrentQuestion({
            _id: "",
            question: "",
            answer: "",
            isActive: true,
          });
          setIsAddDialogOpen(true);
        }}
      />

      <Table
        data={questions}
        columns={columns}
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={limit}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onSearch={handleSearch}
        searchPlaceholder="Buscar pergunta..."
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        className="mt-6"
        isLoading={loading}
      />

      <Drawer open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <QuestionForm
          type="add"
          question={currentQuestion}
          onSave={handleAddQuestion}
          onCancel={() => setIsAddDialogOpen(false)}
        />
      </Drawer>

      <Drawer open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <QuestionForm
          type="edit"
          question={currentQuestion}
          onSave={handleEditQuestion}
          onCancel={() => setIsEditDialogOpen(false)}
        />
      </Drawer>
    </>
  );
}
